import React, { useState, useCallback, useMemo } from 'react';
import styles from "./branchorder.module.css";
import { postFetch } from '../../../fetch/fetch';
import { toast } from 'react-toastify';
import { SwipeableDrawer } from "@mui/material";
import { DataGridPremium, GridColDef, GRID_DETAIL_PANEL_TOGGLE_FIELD } from '@mui/x-data-grid-premium';
import ChatForOpinion from "../controls/chatforOpinion";
import { splitName, Thumb, StatusChip, STATUS_FILTERS, fmtDate, fmtTime, ChatIcon } from './shared';

// 주문내역 — DataGridPremium(compact), 행 클릭 시 상세 패널 펼침, 댓글 수·"선배봇 채팅 열기" → 오른쪽 드로어 (의견 보내기 댓글 드로어 재사용)
const HistoryPage: React.FC<any> = (props: any) => {

    const orders: any[] = props.orders || [];
    const [statusFilter, setStatusFilter] = useState("전체");
    const [branchFilter, setBranchFilter] = useState("전체");
    const [chatOrder, setChatOrder] = useState<any>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [resending, setResending] = useState(0);

    const filterStatuses = (STATUS_FILTERS.find((f) => f[0] === statusFilter) || STATUS_FILTERS[0])[1];
    const list = orders.filter((o) => (!filterStatuses || filterStatuses.includes(o.status)) && (branchFilter === "전체" || o.branchKey === branchFilter));
    const askCount = orders.filter((o) => o.status === "needs_clarification" || o.status === "uncertain").length;
    const branchOptions = props.init.branches.filter((b: any) => orders.some((o) => o.branchKey === b.key));

    const openChat = (o: any) => {
        setChatOrder(o);
        setDrawerOpen(true);
    }

    const toggleRow = (id: any) => {
        props.setOpenOrderId(props.openOrderId === id ? null : id);
    }

    const resendSlack = async (o: any) => {
        setResending(o.id);
        try {
            const result: any = await postFetch(`/api/branch-orders/${o.id}/resend-slack`, {});
            if (result.message === "success") toast.success(`${o.label} 슬랙 재전송 완료`);
            else toast.error(`재전송 실패 (${result.error || result.message})`);
        } catch (e) {
            console.log(e);
            toast.error("재전송 실패");
        }
        setResending(0);
        props.reload();
    }

    const columns: GridColDef[] = [
        { field: "createdAt", headerName: "날짜", width: 118, renderCell: (p) => { const at = new Date(p.row.createdAt); return <span className={styles.num}>{fmtDate(at)} <span className={styles.dash}>{fmtTime(at)}</span></span>; } },
        { field: "branchLabel", headerName: "지점", width: 100 },
        {
            field: "items", headerName: "물품", flex: 1, minWidth: 220, sortable: false,
            valueGetter: (p) => p.row.items[0] ? p.row.items[0].name : "",
            renderCell: (p) => {
                const o = p.row;
                const [n] = splitName(o.items[0] ? o.items[0].name : "");
                return <span className={styles.itemsName} title={o.items[0] ? o.items[0].name : ""}>{n}{o.items.length > 1 ? <span className={styles.more}> 외 {o.items.length - 1}종</span> : null}</span>;
            }
        },
        { field: "qty", headerName: "수량", width: 70, type: "number", align: "left", headerAlign: "left", valueGetter: (p) => p.row.items.reduce((a: number, i: any) => a + i.quantity, 0), renderCell: (p) => <span className={styles.num}>{p.value}개</span> },
        { field: "status", headerName: "상태", width: 130, renderCell: (p) => <StatusChip status={p.row.status} /> },
        { field: "handler", headerName: "처리", width: 90, renderCell: (p) => p.row.handler ? p.row.handler : <span className={styles.dash}>-</span> },
        {
            field: "commentCount", headerName: "댓글", width: 70, type: "number", align: "left", headerAlign: "left",
            renderCell: (p) => <span className={`${styles.cmt} ${p.row.status === "needs_clarification" ? styles.hot : ""}`} onClick={(e) => { e.stopPropagation(); openChat(p.row); }}><ChatIcon />{p.row.commentCount}</span>
        },
        { field: "requesterName", headerName: "요청자", width: 100 },
        { field: "orderNo", headerName: "주문번호", width: 150, renderCell: (p) => p.row.orderNo ? <span className={styles.num}>{p.row.orderNo}</span> : <span className={styles.dash}>-</span> },
    ];

    const getDetailPanelContent = useCallback(({ row }: any) => (
        <DetailPanel order={row} openChat={openChat} resendSlack={resendSlack} resending={resending === row.id} canResend={row.requesterId === props.init.userId || props.init.isAdmin} />
    ), [resending, props.init]);

    const expandedIds = useMemo(() => props.openOrderId && list.some((o) => o.id === props.openOrderId) ? [props.openOrderId] : [], [props.openOrderId, list]);

    const chatTitle = (o: any) => {
        if (!o) return "";
        const [n] = splitName(o.items[0] ? o.items[0].name : "");
        return `${o.label} · ${o.branchLabel} · ${n}${o.items.length > 1 ? ` 외 ${o.items.length - 1}종` : ""}`;
    }

    return (
        <section>
            <div className={styles.sec}>
                <h2>주문내역</h2>
                <span className={styles.sub}>요청별 상태·질문·답변·주문번호를 확인합니다</span>
                <div className={styles.right}>
                    <button type="button" className={styles.btn} onClick={() => { props.reload(); toast("주문내역을 갱신했습니다.", { autoClose: 1500 }); }}>
                        <svg viewBox="0 0 24 24"><path d="M20 12a8 8 0 1 1-2.3-5.7M20 4v5h-5" /></svg>새로고침
                    </button>
                </div>
            </div>
            <div className={styles.segWrap}>
                <div className={`${styles.seg} ${styles.compact}`} aria-label="상태 필터">
                    {STATUS_FILTERS.map(([n]) => (
                        <button key={n} type="button" className={n === statusFilter ? styles.on : ""} onClick={() => setStatusFilter(n)}>
                            {n}{n === "확인 필요" ? <span className={`${styles.count} ${styles.num}`} style={{ marginLeft: 4 }}>{askCount}</span> : null}
                        </button>
                    ))}
                </div>
                <div className={`${styles.seg} ${styles.compact}`} aria-label="지점 필터">
                    {[{ key: "전체", label: "전체" }, ...branchOptions].map((b: any) => (
                        <button key={b.key} type="button" className={b.key === branchFilter ? styles.on : ""} onClick={() => setBranchFilter(b.key)}>{b.label}</button>
                    ))}
                </div>
            </div>
            <div className={styles.panel} style={{ padding: 0, height: "calc(100vh - 300px)", minHeight: 480 }}>
                <DataGridPremium
                    rows={list}
                    columns={columns}
                    density="compact"
                    disableRowSelectionOnClick
                    disableColumnMenu
                    hideFooterSelectedRowCount
                    columnVisibilityModel={{ [GRID_DETAIL_PANEL_TOGGLE_FIELD]: false }}
                    onRowClick={(p) => toggleRow(p.id)}
                    getDetailPanelContent={getDetailPanelContent}
                    getDetailPanelHeight={() => 'auto'}
                    detailPanelExpandedRowIds={expandedIds}
                    onDetailPanelExpandedRowIdsChange={(ids) => props.setOpenOrderId(ids.length ? ids[ids.length - 1] : null)}
                    getRowClassName={(p) => `${p.row.status === "needs_clarification" ? "bo-asking" : ""} ${p.id === props.openOrderId ? "bo-open" : ""}`}
                    localeText={{ noRowsLabel: "해당 조건의 주문 요청이 없습니다." }}
                    sx={{
                        border: "none",
                        fontSize: 13.5,
                        color: "var(--fg)",
                        "& .MuiDataGrid-columnHeaders": { backgroundColor: "#fafafa", borderBottom: "1px solid var(--line)" },
                        "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600, fontSize: 13 },
                        "& .MuiDataGrid-row": { cursor: "pointer" },
                        "& .MuiDataGrid-row:hover": { backgroundColor: "#f8fafc" },
                        "& .MuiDataGrid-row.bo-asking": { backgroundColor: "#fff8ec" },
                        "& .MuiDataGrid-row.bo-open": { backgroundColor: "#f3f7fb" },
                        "& .MuiDataGrid-cell": { borderBottom: "1px solid var(--line-soft)" },
                        "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": { outline: "none" },
                        "& .MuiDataGrid-detailPanel": { backgroundColor: "#fafbfc", borderBottom: "1px solid var(--line)" },
                    }}
                />
            </div>

            <SwipeableDrawer
                anchor="right"
                open={drawerOpen}
                onClose={() => { setDrawerOpen(false); setChatOrder(null); }}
                onOpen={() => setDrawerOpen(true)}
            >
                <div style={{ width: "550px", height: "100%" }}>
                    <ChatForOpinion
                        handleUpdate2={() => { }}
                        handleUpdate={() => { props.reload(); }}
                        openControl={drawerOpen}
                        user={props.user}
                        studentName={chatTitle(chatOrder)}
                        chatRoomId={chatOrder ? chatOrder.id : 0}
                        messageUpdate={props.branchOrderUpdate}
                        unreadMessage={() => { }}
                        messageApiEndpoint="https://peetsunbae.com/api/branch-orders/comments"
                    />
                </div>
            </SwipeableDrawer>
        </section>
    );
}

const DetailPanel: React.FC<any> = (props: any) => {
    const o = props.order;
    const asking = o.status === "needs_clarification";
    const at = new Date(o.createdAt);
    return (
        <div className={styles.detailGrid} style={{ cursor: "default" }}>
            <div className={styles.dcol}>
                <h4>요청 물품 <span className={styles.mini}>{o.label}</span></h4>
                <div className={styles.dlist}>
                    {o.items.map((it: any) => {
                        const [n, op] = splitName(it.name);
                        return (
                            <div key={it.id} className={styles.ditem}>
                                <div className={styles.dThumb}><Thumb image={it.image} category={it.category} /></div>
                                <div><div className={styles.ditemName}><a className={styles.link} style={{ color: "inherit", fontSize: 13 }} href={it.url} target="_blank" rel="noopener noreferrer">{n}</a></div><div className={styles.ditemOpt}>{op || it.category}</div></div>
                                <div className={`${styles.ditemQ} ${styles.num}`}>{it.quantity}개</div>
                            </div>
                        );
                    })}
                </div>
                <dl className={styles.kv}>
                    <dt>요청자</dt><dd>{o.requesterName}</dd>
                    <dt>요청 시각</dt><dd className={styles.num}>{fmtDate(at)} {fmtTime(at)}</dd>
                    <dt>메모</dt><dd>{o.memo ? o.memo : <span className={styles.dash}>-</span>}</dd>
                    {o.orderNo ? <><dt>주문번호</dt><dd className={styles.num}>{o.orderNo}</dd></> : null}
                    {o.totalAmount !== null && o.totalAmount !== undefined ? <><dt>결제 금액</dt><dd className={styles.num}>{(+o.totalAmount).toLocaleString()}원</dd></> : null}
                    {o.eta ? <><dt>배송</dt><dd>{o.eta} 도착 예정</dd></> : null}
                </dl>
                <details className={styles.slack}>
                    <summary>슬랙 전달 메시지 <span className={styles.mini}>#수능선배-업무봇 · {o.slackTs ? "스레드 연결됨" : o.slackError ? "전달 실패" : "전달 중"}</span></summary>
                    <pre>{o.slackText || "-"}</pre>
                    <div className={styles.slackMeta}>{o.slackTs ? `chat.postMessage → thread_ts ${o.slackTs} · 질문·답변·주문완료는 이 스레드에 이어서 전송` : "아직 슬랙 스레드가 없습니다"}</div>
                </details>
            </div>
            <div className={styles.dcol}>
                <h4>처리 현황 {asking ? <span className={`${styles.st} ${styles.ask}`} style={{ height: 22, fontSize: 12 }}>답변 필요</span> : null}</h4>
                <dl className={styles.kv} style={{ marginTop: 0 }}>
                    <dt>상태</dt><dd><StatusChip status={o.status} /></dd>
                    <dt>처리</dt><dd>{o.handler || "-"}</dd>
                    <dt>최근 메시지</dt><dd>{o.lastMessage ? `${o.lastMessage.name ? o.lastMessage.name + ": " : ""}${("" + o.lastMessage.message).split("\n")[0]}` : "-"}</dd>
                    {o.status === "failed" && o.failReason ? <><dt>실패 사유</dt><dd>{o.failReason}</dd></> : null}
                </dl>
                <div style={{ marginTop: 12 }}>
                    <button type="button" className={styles.openchat} onClick={() => props.openChat(o)}><ChatIcon />선배봇 채팅 열기 ({o.commentCount})</button>
                </div>
                {asking ? <div className={styles.askBanner}>선배봇이 확인 질문을 남겼습니다. 채팅에서 답변해 주세요.</div> : null}
                {o.status === "uncertain" ? <div className={styles.askBanner}>선배봇이 주문 완료를 보고했지만 주문번호가 확인되지 않았습니다. 쿠팡 주문내역을 사람이 확인해 주세요.</div> : null}
                {
                    !o.slackTs && o.slackError ?
                        <div className={styles.failBanner}>
                            <span>슬랙 전달 실패 — 선배봇이 아직 요청을 받지 못했습니다. ({o.slackError})</span>
                            {props.canResend ? <button type="button" className={styles.btn} style={{ height: 28 }} disabled={props.resending} onClick={() => props.resendSlack(o)}>{props.resending ? "전송 중…" : "재전송"}</button> : null}
                        </div>
                        : null
                }
            </div>
        </div>
    );
}

export default HistoryPage;
